import React from "react";
import { Button } from "@/components/ui/button";
import { LanguageToggle } from "@/components/ui/LanguageToggle";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Settings, ArrowUpDown, Check, Users } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";
import { User } from "@/integrations/neon";

interface DashboardHeaderProps {
  user: User;
  showParticipantsView: boolean;
  sortOption: "created_desc" | "created_asc" | "average_desc" | "completion_desc";
  sortLabel: string;
  onToggleParticipantsView: () => void;
  onSortChange: (option: "created_desc" | "created_asc" | "average_desc" | "completion_desc") => void;
  onAddScript: () => void;
  onSettings: () => void;
  onSignOut: () => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  user,
  showParticipantsView,
  sortOption,
  sortLabel,
  onToggleParticipantsView,
  onSortChange,
  onAddScript,
  onSettings,
  onSignOut,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border/80 bg-card/85 backdrop-blur-md shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-xl border border-primary/20 shadow-2xs">
                <img
                  src="/lovable-uploads/30cb2c33-4a20-4489-831b-007a49a4be5f.png"
                  alt={t("dashboard.logo_alt")}
                  className="h-8 w-8 object-contain"
                />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground tracking-tight">{t("app.name")}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border/80">
                {user.email}
              </span>
              <LanguageToggle className="w-auto" />
              <Button
                variant="ghost"
                size="icon"
                onClick={onSettings}
                className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors rounded-lg"
                title={t("dashboard.settings")}
              >
                <Settings className="h-4.5 w-4.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onSignOut}
                className="text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted border-border transition-colors rounded-lg"
              >
                {t("dashboard.sign_out")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground mb-1">{t("dashboard.scripts_title")}</h2>
            <p className="text-muted-foreground text-base">
              {t("dashboard.scripts_subtitle")}
            </p>
          </div>

          <div className="flex flex-col gap-2.5 w-full sm:w-auto sm:flex-row sm:flex-nowrap sm:items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 transition-all duration-200 w-full sm:w-auto justify-between sm:justify-center bg-card border-border/90 text-foreground hover:bg-muted shadow-2xs font-medium rounded-xl"
                >
                  <ArrowUpDown className="h-4 w-4 mr-2 text-muted-foreground" />
                  {sortLabel}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-xl shadow-lg border-border">
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onSortChange("created_desc")}
                >
                  <span className="flex items-center gap-2">
                    {sortOption === "created_desc" && <Check className="h-3.5 w-3.5" />}
                    {t("dashboard.sort_menu.created_desc")}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onSortChange("created_asc")}
                >
                  <span className="flex items-center gap-2">
                    {sortOption === "created_asc" && <Check className="h-3.5 w-3.5" />}
                    {t("dashboard.sort_menu.created_asc")}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onSortChange("average_desc")}
                >
                  <span className="flex items-center gap-2">
                    {sortOption === "average_desc" && <Check className="h-3.5 w-3.5" />}
                    {t("dashboard.sort_menu.average_desc")}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onSortChange("completion_desc")}
                >
                  <span className="flex items-center gap-2">
                    {sortOption === "completion_desc" && <Check className="h-3.5 w-3.5" />}
                    {t("dashboard.sort_menu.completion_desc")}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant={showParticipantsView ? "secondary" : "outline"}
              onClick={onToggleParticipantsView}
              className="h-10 transition-all duration-200 w-full sm:w-auto justify-center bg-card border-border/90 text-foreground hover:bg-muted shadow-2xs font-medium rounded-xl"
            >
              <Users className="h-4 w-4 mr-2 text-muted-foreground" />
              {showParticipantsView
                ? t("dashboard.view_toggle.basic")
                : t("dashboard.view_toggle.advanced")}
            </Button>
            <Button
              onClick={onAddScript}
              className="h-10 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200 rounded-xl w-full sm:w-auto justify-center px-5 font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("dashboard.add_script")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
